/**
 * wizardSlice.js — Redux Toolkit slice owning the CMS Self Signup wizard workflow state.
 *
 * This slice is the single source of truth for the wizard's structured,
 * frequently-mutated, reset-able state (design "State Management"):
 *  - `currentStep` — the step the Applicant is actively on.
 *  - `completedSteps` — which steps recorded a positive Success_Indicator (Req 2.6).
 *  - `token` — the single latest-wins rotating token (Req 3.3, 3.4, 3.9).
 *  - `authTokenEncrypted` — GetAuthorization token value + issue timestamp (Req 5.5, 5.8).
 *  - `verificationId` — DigiLocker verification id (Req 7.3).
 *  - per-step form `data` and per-step `stepStatus`.
 *  - selected location ids/names (state/district/city).
 *  - `lastError` — the most recent normalized error (Req 3.8).
 *
 * Security/privacy (Req 13.4, 13.6): the slice is intentionally NOT wrapped in
 * redux-persist and performs NO localStorage/sessionStorage writes. All state
 * lives only in memory and `reset` returns the initial (empty) state so
 * completing/cancelling/restarting the wizard clears sensitive fields.
 *
 * ---------------------------------------------------------------------------
 * Design decision — `completedSteps` as a serializable object map.
 * The design WizardState typedef describes `completedSteps` as a `Set<StepId>`.
 * A JS `Set` is NOT serializable and violates Redux's serializable-state
 * contract (and would break time-travel/devtools). We therefore represent
 * completion as a plain object map `{ [stepId]: true }` (only truthy entries
 * are kept). This preserves the Set's semantics — membership test, add, delete —
 * while remaining serializable. `state/selectors.js` reads this map when
 * computing gating (`earliestIncompleteStep`).
 * ---------------------------------------------------------------------------
 */

import { createSlice } from '@reduxjs/toolkit';
import { STEP_IDS, STEP_ORDER, STEP_DEPENDENCIES } from '../constants/steps';
import { reduceToken } from '../utils/tokenStore';

/**
 * Build the empty per-step form-data container. Each step owns only the fields
 * it collects (design data-model typedefs). Kept as a factory so `reset` and
 * the initial state never share a mutable reference.
 *
 * @returns {Object} fresh per-step form data
 */
function createInitialData() {
    return {
        phone: { phone: '' },
        email: { email: '' },
        pan: { pancard: '', pancard_name: '' },
        aadhaar: { aadhaar: '' },
        selfie: { selfieBase64: null },
        bank: { accountno: '', ifsc: '', bank_holder_name: '' }
    };
}

/**
 * Build the empty per-step status map. Every step starts `idle`.
 *
 * @returns {Object<string, 'idle'|'pending'|'success'|'failed'>}
 */
function createInitialStepStatus() {
    return STEP_ORDER.reduce((acc, stepId) => {
        acc[stepId] = 'idle';
        return acc;
    }, {});
}

/**
 * Build the empty selected-location container (ids + display names).
 *
 * @returns {Object}
 */
function createInitialLocation() {
    return {
        stateId: null,
        districtId: null,
        cityId: null,
        stateName: null,
        districtName: null,
        cityName: null
    };
}

/**
 * The initial (empty) wizard state. Returned verbatim by `reset` (Req 13.6).
 * Built through factories so no nested object is shared between resets.
 *
 * @returns {Object} initial wizard state
 */
export function createInitialState() {
    return {
        currentStep: STEP_ORDER[0],
        // Serializable completion map: presence of a truthy value === completed.
        completedSteps: {},
        // Single latest-wins rotating token (memory only).
        token: null,
        // GetAuthorization encrypted token + issue timestamp (Req 5.5, 5.8).
        authTokenEncrypted: { value: null, issuedAt: null },
        // email_token issued by EmailVerification, consumed by OtpEmailVerification/Resend
        // (in-memory only, never persisted — Req 13.4, 13.6).
        emailToken: null,
        // Encrypted token issued by OtpEmailVerification, passed AS the token to
        // GetAuthorization (< 300s freshness enforced by the email step hook). In-memory only.
        otpEncryptedToken: { value: null, issuedAt: null },
        // DigiLocker verification id (Req 7.3).
        verificationId: null,
        // DigiLocker `Name` from the status-check response — the `target` for verifyAadharNames.
        aadhaarName: null,
        // DigiLocker document `Image` (url/base64) from the status-check response — used as
        // image2 (reference) for the SELFIEAGENT face-similarity check. In-memory only.
        aadhaarImageUrl: null,
        data: createInitialData(),
        stepStatus: createInitialStepStatus(),
        location: createInitialLocation(),
        lastError: null
    };
}

const initialState = createInitialState();

/**
 * Map a step data-key to the specific field keys within that step object that
 * represent identity-bearing values. Mutations to non-identity keys (such as
 * uploaded document image attachments like `aadharfront`, `aadharback`, or `pancard_img`)
 * do NOT trigger dependency-based invalidation of completed verification steps.
 *
 * @type {Object<string, ReadonlyArray<string>>}
 */
const IDENTITY_KEYS_BY_STEP = {
    phone: ['phone'],
    email: ['email'],
    pan: ['pancard', 'pancard_name'],
    aadhaar: ['aadhaar'],
    selfie: ['selfieBase64'],
    bank: ['accountno', 'ifsc']
};

/**
 * Clear the completion + status for every step that depends on `identityField`
 * (Req 2.9 / Property 8). Only dependent steps are touched; unrelated steps and
 * their entered data are left intact.
 *
 * Mutates the RTK Immer draft in place.
 *
 * @param {Object} state Immer draft of the wizard state
 * @param {string} identityField one of constants/steps IDENTITY_FIELDS values
 */
function invalidateDependents(state, identityField) {
    const dependents = STEP_DEPENDENCIES[identityField];
    if (!dependents) {
        return;
    }
    dependents.forEach((stepId) => {
        if (state.completedSteps[stepId]) {
            delete state.completedSteps[stepId];
        }
        state.stepStatus[stepId] = 'idle';
    });
}

const wizardSlice = createSlice({
    name: 'cmsWizard',
    initialState,
    reducers: {
        /**
         * Latest-wins token replacement after a normalized response (Req 3.3,
         * 3.4, 3.9). Reuses the pure `reduceToken` helper: when the response
         * carries a non-null token the stored token is replaced, otherwise it
         * is left unchanged. Accepts either a normalized response object
         * (`{ token }`) or a bare token payload.
         *
         * @param {Object} action.payload normalized response (or `{ token }`)
         */
        tokenRotated(state, action) {
            state.token = reduceToken(state.token, action.payload);
        },

        /**
         * Store the GetAuthorization encrypted token verbatim along with its
         * issue timestamp for the 300s age check (Req 5.5, 5.6, 5.8). The token
         * is never decrypted or altered (Req 13.3).
         *
         * @param {Object} action.payload
         * @param {string|null} action.payload.value encrypted token, stored as-is
         * @param {number|null} [action.payload.issuedAt] issue time (ms epoch); defaults to now
         */
        authTokenEncryptedSet(state, action) {
            const { value = null, issuedAt } = action.payload || {};
            state.authTokenEncrypted = {
                value,
                issuedAt: issuedAt == null ? Date.now() : issuedAt
            };
        },

        /**
         * Store the `email_token` issued by EmailVerification (in-memory only, Req 13.4). It is
         * later sent by OtpEmailVerification and ResendEmailVerification.
         *
         * @param {Object} action.payload the email_token string (or `{ emailToken }`)
         */
        emailTokenSet(state, action) {
            const payload = action.payload;
            state.emailToken = payload && typeof payload === 'object' ? payload.emailToken ?? null : payload ?? null;
        },

        /**
         * Store the ENCRYPTED token issued by OtpEmailVerification verbatim along with its issue
         * timestamp (Req 5.6, 5.8, 13.3). This value is passed AS the `token` to GetAuthorization
         * and is subject to the 300s freshness window. In-memory only.
         *
         * @param {Object} action.payload
         * @param {string|null} action.payload.value encrypted token, stored as-is
         * @param {number|null} [action.payload.issuedAt] issue time (ms epoch); defaults to now
         */
        otpEncryptedTokenSet(state, action) {
            const { value = null, issuedAt } = action.payload || {};
            state.otpEncryptedToken = {
                value,
                issuedAt: issuedAt == null ? Date.now() : issuedAt
            };
        },

        /**
         * Store the DigiLocker `Name` from the status-check response — the `source` name for
         * verifyAadharNames (Req 7.8). In-memory only.
         *
         * @param {Object} action.payload the name string (or `{ aadhaarName }`)
         */
        aadhaarNameSet(state, action) {
            const payload = action.payload;
            state.aadhaarName = payload && typeof payload === 'object' ? payload.aadhaarName ?? null : payload ?? null;
        },

        /**
         * Store the DigiLocker document image (url/base64) from the status-check response — the
         * reference image (image2) for the SELFIEAGENT face-similarity check. In-memory only.
         *
         * @param {Object} action.payload the image url/base64 string (or `{ aadhaarImageUrl }`)
         */
        aadhaarImageUrlSet(state, action) {
            const payload = action.payload;
            state.aadhaarImageUrl = payload && typeof payload === 'object' ? payload.aadhaarImageUrl ?? null : payload ?? null;
        },

        /**
         * Store the DigiLocker verification id (Req 7.3).
         *
         * @param {Object} action.payload the verification id string (or `{ verificationId }`)
         */
        verificationIdSet(state, action) {
            const payload = action.payload;
            state.verificationId = payload && typeof payload === 'object' ? payload.verificationId ?? null : payload ?? null;
        },

        /**
         * Mark the requested step as `pending` and clear any prior error so the
         * UI can reflect an in-flight request. Does not change `currentStep`.
         *
         * @param {Object} action.payload the StepId being requested
         */
        stepPending(state, action) {
            const stepId = action.payload;
            if (stepId in state.stepStatus) {
                state.stepStatus[stepId] = 'pending';
            }
            state.lastError = null;
        },

        /**
         * Set a step's status ('idle' | 'pending' | 'success' | 'failed')
         * without marking it complete in `completedSteps` or advancing `currentStep`.
         */
        stepStatusSet(state, action) {
            const { step, status } = action.payload || {};
            if (step && step in state.stepStatus && status) {
                state.stepStatus[step] = status;
            }
        },

        /**
         * Record a positive Success_Indicator for a step (Req 2.6) and advance
         * (Req 2.2). Retains all entered form data. Optionally rotates the token
         * from the same normalized response so callers can complete + rotate in
         * one dispatch (Req 3.3).
         *
         * Advancing sets `currentStep` to the next step in STEP_ORDER; the last
         * step keeps `currentStep` unchanged (nothing follows KYC).
         *
         * @param {Object} action.payload
         * @param {string} action.payload.step the completed StepId
         * @param {Object} [action.payload.response] normalized response carrying a rotating token
         */
        stepSucceeded(state, action) {
            const { step, response } = action.payload || {};
            if (!step || !(step in state.stepStatus)) {
                return;
            }

            // Latest-wins token rotation from this response (Req 3.3, 3.4, 3.9).
            if (response !== undefined) {
                state.token = reduceToken(state.token, response);
            }

            state.completedSteps[step] = true;
            state.stepStatus[step] = 'success';
            state.lastError = null;

            // Advance to the next step in documented order (Req 2.2).
            const idx = STEP_ORDER.indexOf(step);
            if (idx >= 0 && idx < STEP_ORDER.length - 1) {
                state.currentStep = STEP_ORDER[idx + 1];
            }
        },

        /**
         * Record a step failure (Req 3.8, 3.10). The Applicant stays on the
         * current step, all entered data is retained (this reducer never mutates
         * `state.data`), and the normalized error — which identifies the failed
         * step — is stored in `lastError`. The token may still rotate if the
         * failing response carried a new one (backend rotates on failures too).
         *
         * @param {Object} action.payload
         * @param {import('../api/normalizeError').NormalizedError} action.payload.error normalized error
         * @param {Object} [action.payload.response] normalized response that may carry a rotating token
         */
        stepFailed(state, action) {
            const { error = null, response } = action.payload || {};

            if (response !== undefined) {
                state.token = reduceToken(state.token, response);
            }

            const failedStep = error && error.step ? error.step : null;
            if (failedStep && failedStep in state.stepStatus) {
                state.stepStatus[failedStep] = 'failed';
            }
            state.lastError = error;
            // currentStep intentionally unchanged; data intentionally untouched.
        },

        /**
         * Update a step's entered form data, merging into the existing step
         * object so untouched fields are retained (Req 2.8). When the mutated
         * field is an identity field whose value actually changes, dependent
         * later steps are invalidated (Req 2.9 / Property 8).
         *
         * @param {Object} action.payload
         * @param {string} action.payload.step the StepId whose data-key to update
         * @param {Object} action.payload.values partial values to merge
         */
        formDataChanged(state, action) {
            const { step, values } = action.payload || {};
            if (!step || !values || !(step in state.data)) {
                return;
            }

            const previous = state.data[step];
            const identityKeys = IDENTITY_KEYS_BY_STEP[step];
            // Detect whether an identity-bearing value actually changed.
            const identityChanged =
                Boolean(identityKeys) && Object.keys(values).some((key) => identityKeys.includes(key) && previous[key] !== values[key]);

            state.data[step] = { ...previous, ...values };

            if (identityChanged) {
                invalidateDependents(state, step);
            }
        },

        /**
         * Explicit dependency-based invalidation (Req 2.9). Clears the
         * Success_Indicator (completion + status) for exactly the steps that
         * depend on the given identity field. Use when an identity field changes
         * outside of `formDataChanged` (e.g. a re-verification flow).
         *
         * @param {Object} action.payload
         * @param {string} action.payload.field one of IDENTITY_FIELDS values
         */
        identityFieldChanged(state, action) {
            const { field } = action.payload || {};
            if (field) {
                invalidateDependents(state, field);
            }
        },

        /**
         * Set the active step. Callers (the wizard shell) are responsible for
         * gating; this reducer simply records the requested step.
         *
         * @param {Object} action.payload the StepId to make current
         */
        stepChanged(state, action) {
            state.currentStep = action.payload;
        },

        /**
         * Update selected location ids/names, merging so partial cascade updates
         * (e.g. only the state) retain the rest.
         *
         * @param {Object} action.payload partial LocationData
         */
        locationChanged(state, action) {
            state.location = { ...state.location, ...(action.payload || {}) };
        },

        /**
         * Clear the last normalized error without touching any other state.
         */
        errorCleared(state) {
            state.lastError = null;
        },

        /**
         * Reset the whole slice to the initial (empty) state (Req 13.6). Used on
         * completion, cancellation, restart, and on wizard mount (Req 1.2, 1.5).
         * Returning a fresh initial state guarantees tokens, KYC data, Aadhaar,
         * and all sensitive in-memory fields are cleared.
         *
         * @returns {Object} a brand-new initial state
         */
        reset() {
            return createInitialState();
        }
    }
});

export const {
    tokenRotated,
    authTokenEncryptedSet,
    emailTokenSet,
    otpEncryptedTokenSet,
    aadhaarNameSet,
    aadhaarImageUrlSet,
    verificationIdSet,
    stepPending,
    stepStatusSet,
    stepSucceeded,
    stepFailed,
    formDataChanged,
    identityFieldChanged,
    stepChanged,
    locationChanged,
    errorCleared,
    reset
} = wizardSlice.actions;

export { STEP_IDS };

export default wizardSlice.reducer;
